import QuizRubricOptions from 'components/CreateQuiz/QuizRubricOptions';
import { PageContext } from 'pages/CreateQuiz/PageProvider';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, IconButton, Stack, styled, Text } from 'shell/components';
import ViewRubricTable, { useViewAccordionRubricTableButton } from 'shell/components/ViewRubricTable';
import { hasQuizRubric } from 'utils/quizRubric';

const RubricSettingsSection = ({ device }) => {
    const { t } = useTranslation(['quiz', 'assignment']);
    const {
        form,
        setForm,
        setRubricDialog,
        handleDeleteRubric,
        handleRubricTypeChange,
        handleMapCriteriaByOrderChange,
    } = useContext(PageContext);
    const { AccordionRubricTableButton, isExpanded } = useViewAccordionRubricTableButton();
    const hasRubric = hasQuizRubric(form.rubric);

    return (
        <SettingsCard component={'section'} aria-labelledby={'quiz-rubric-heading'} spacing={4}>
            <Stack
                direction={device === 'mobile' ? 'column' : 'row'}
                alignItems={device === 'mobile' ? 'flex-start' : 'center'}
                justifyContent={'space-between'}
                spacing={4}
            >
                <Stack spacing={4} sx={{ minWidth: 0 }}>
                    <Text component={'h3'} id={'quiz-rubric-heading'} variant={'p1'} weight={'medium'}>
                        {t('rubric.title', 'Quiz Rubric')}
                    </Text>
                    {!hasRubric && (
                        <Text role={'alert'} color={'error'}>
                            {t(
                                'rubric.missing',
                                'Rubric missing. You can either create a new rubric or reimport the quiz with the rubric included. Without this, the quiz cannot be created.'
                            )}
                        </Text>
                    )}
                </Stack>
                {!hasRubric && (
                    <Button onClick={() => setRubricDialog('create')} sx={{ flexShrink: 0 }}>
                        {t('rubric.create', 'Create rubric')}
                    </Button>
                )}
            </Stack>
            {hasRubric && (
                <>
                    <Stack spacing={2}>
                        <Text color={'light'}>{t('assignment:rubric.title-rubric', 'Rubric')}</Text>
                        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} spacing={2}>
                            <Text variant={'sh1'} weight={'semibold'} sx={{ overflowWrap: 'anywhere', minWidth: 0 }}>
                                {form.rubric.title}
                            </Text>
                            <Stack direction={'row'} spacing={2} sx={{ flexShrink: 0 }}>
                                <IconButton
                                    aria-label={t('assignment:general-section.edit-rubric', 'Edit rubric')}
                                    kind={'edit'}
                                    size={'small'}
                                    tooltip={{ component: t('common:controls.edit', 'Edit') }}
                                    variant={'square'}
                                    onClick={() => setRubricDialog('edit')}
                                />
                                <IconButton
                                    aria-label={t('assignment:general-section.delete-rubric', 'Delete rubric')}
                                    kind={'delete'}
                                    size={'small'}
                                    tooltip={{ component: t('common:controls.delete', 'Delete') }}
                                    variant={'square'}
                                    onClick={handleDeleteRubric}
                                />
                            </Stack>
                        </Stack>
                    </Stack>
                    <QuizRubricOptions
                        device={device}
                        form={form}
                        setForm={setForm}
                        handleRubricTypeChange={handleRubricTypeChange}
                        handleMapCriteriaByOrderChange={handleMapCriteriaByOrderChange}
                    />
                    <Stack spacing={2}>
                        <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} spacing={2}>
                            <Text>{t('rubric.title', 'Quiz Rubric')}</Text>
                            <AccordionRubricTableButton />
                        </Stack>
                        <ViewRubricTable rubric={form.rubric} data={form.rubric.criteria} isExpanded={isExpanded} />
                    </Stack>
                </>
            )}
        </SettingsCard>
    );
};

const SettingsCard = styled(Stack)(({ theme }) => ({
    padding: theme.spacing(3),
    border: `1px solid ${theme.palette.ensightful.neutral[300]}`,
    borderRadius: '16px',
}));

export default RubricSettingsSection;
